import React, { useState, useEffect, useCallback } from 'react';
import { 
  Row, Col, Card, Spin, Input, Space, Tag, Button, Typography, message, List, Checkbox
} from 'antd';
import { 
  FolderOutlined, FolderOpenOutlined, UserOutlined, PlusOutlined, DeleteOutlined
} from '@ant-design/icons';
import api from '../services/api';

const { Search } = Input;
const { Text } = Typography;

const DepartmentEmployeeTransfer = ({ 
  selectedEmployeeIds = [],
  onSelectionChange,
  titles = ['可选成员', '已选成员']
}) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(selectedEmployeeIds);
  const [employeeMap, setEmployeeMap] = useState(new Map()); // departmentId -> employees
  const [expandedDepts, setExpandedDepts] = useState(new Set());

  // 加载部门树
  const loadDepartments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/wework/departments/tree');
      setDepartments(response.data || []);
    } catch (error) {
      console.error('加载部门失败:', error);
      message.error('加载部门失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 全局员工数据缓存
  const [allEmployees, setAllEmployees] = useState([]);
  const [allEmployeesLoaded, setAllEmployeesLoaded] = useState(false);

  // 加载所有员工数据（一次性加载，后续从中筛选）
  const loadAllEmployees = useCallback(async () => {
    if (allEmployeesLoaded) return allEmployees;
    
    try {
      const response = await api.get('/wework/employees', {
        params: {
          page: 0,
          size: 2000 // 获取所有员工
        }
      });
      
      if (response.data.content) {
        const employees = response.data.content || [];
        setAllEmployees(employees);
        setAllEmployeesLoaded(true);
        return employees;
      }
    } catch (error) {
      console.error('加载所有员工失败:', error);
    }
    return [];
  }, [allEmployees, allEmployeesLoaded]);

  // 加载部门员工（只获取主部门为指定部门的员工）
  const loadDepartmentEmployees = useCallback(async (departmentId) => {
    if (employeeMap.has(departmentId)) {
      return employeeMap.get(departmentId);
    }
    
    try {
      // 确保全量员工数据已加载
      const employees = await loadAllEmployees();
      
      // 筛选主部门为指定部门的员工
      const departmentEmployees = employees.filter(emp => emp.mainDepartment === departmentId);
      
      setEmployeeMap(prev => new Map(prev.set(departmentId, departmentEmployees)));
      return departmentEmployees;
    } catch (error) {
      console.error('加载部门员工失败:', error);
    }
    return [];
  }, [employeeMap, loadAllEmployees]);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  // 预加载的员工缓存，用于存储已选员工的完整信息
  const [preloadedEmployees, setPreloadedEmployees] = useState(new Map()); // employeeId -> employee

  // 预加载已选成员的数据
  const preloadSelectedEmployeesData = useCallback(async (employeeIds) => {
    if (employeeIds.length === 0) return;
    
    try {
      // 使用全局员工数据
      const employees = await loadAllEmployees();
      // 找出已选员工
      const selectedEmployees = employees.filter(emp => employeeIds.includes(emp.id));
      
      // 存储到预加载缓存中
      const newPreloadedMap = new Map();
      selectedEmployees.forEach(emp => {
        newPreloadedMap.set(emp.id, emp);
      });
      setPreloadedEmployees(newPreloadedMap);
      
      console.log('预加载员工数据:', selectedEmployees.length, '个员工');
    } catch (error) {
      console.error('预加载已选成员数据失败:', error);
    }
  }, [loadAllEmployees]);

  useEffect(() => {
    setSelectedKeys(selectedEmployeeIds);
    // 当有已选成员时，需要预加载这些成员的信息以便在右侧显示
    if (selectedEmployeeIds.length > 0) {
      preloadSelectedEmployeesData(selectedEmployeeIds);
    }
  }, [selectedEmployeeIds, preloadSelectedEmployeesData]);

  // 当输入搜索关键词时，确保已加载全量员工数据以支持全局搜索
  useEffect(() => {
    if (searchKeyword && !allEmployeesLoaded) {
      loadAllEmployees();
    }
  }, [searchKeyword, allEmployeesLoaded, loadAllEmployees]);

  // 递归构建数据源
  const buildDataSource = useCallback((depts, level = 0) => {
    const items = [];
    
    depts.forEach(dept => {
      // 部门项
      items.push({
        key: `dept_${dept.id}`,
        title: dept.name,
        isDepartment: true,
        departmentId: dept.id,
        level
      });

      // 如果部门已展开，添加其内容
      if (expandedDepts.has(dept.id)) {
        // 添加员工
        if (employeeMap.has(dept.id)) {
          const employees = employeeMap.get(dept.id);
          employees.forEach(emp => {
            const isInactive = emp.systemStatus !== 1 || emp.status !== 1;
            items.push({
              key: emp.id.toString(),
              title: emp.name,
              description: emp.position || '暂无职位',
              isDepartment: false,
              employee: emp,
              level: level + 1,
              isInactive
            });
          });
        }
        
        // 添加子部门（只有在父部门展开时才显示）
        if (dept.children && dept.children.length > 0) {
          items.push(...buildDataSource(dept.children, level + 1));
        }
      }
    });

    return items;
  }, [employeeMap, expandedDepts]);

  // 处理部门展开/收缩
  const toggleDepartment = async (departmentId) => {
    const newExpanded = new Set(expandedDepts);
    if (expandedDepts.has(departmentId)) {
      newExpanded.delete(departmentId);
    } else {
      newExpanded.add(departmentId);
      await loadDepartmentEmployees(departmentId);
    }
    setExpandedDepts(newExpanded);
  };

  // 添加整个部门（只添加直属员工）
  const addWholeDepartment = async (departmentId) => {
    const employees = await loadDepartmentEmployees(departmentId);
    if (employees.length > 0) {
      const employeeIds = employees.map(emp => emp.id);
      const newSelectedKeys = [...new Set([...selectedKeys, ...employeeIds])];
      setSelectedKeys(newSelectedKeys);
      onSelectionChange && onSelectionChange(newSelectedKeys);
      message.success(`已添加 ${employees.length} 个直属成员`);
    } else {
      message.info('该部门没有直属成员');
    }
  };

  // 处理员工选择
  const handleEmployeeSelect = (employeeId, checked) => {
    const newSelectedKeys = checked 
      ? [...selectedKeys, employeeId]
      : selectedKeys.filter(id => id !== employeeId);
    
    setSelectedKeys(newSelectedKeys);
    onSelectionChange && onSelectionChange(newSelectedKeys);
  };

  // 移除选中的员工
  const handleRemoveEmployee = (employeeId) => {
    const newSelectedKeys = selectedKeys.filter(id => id !== employeeId);
    setSelectedKeys(newSelectedKeys);
    onSelectionChange && onSelectionChange(newSelectedKeys);
  };

  // 获取所有员工数据用于右侧显示
  const getAllEmployeesData = () => {
    const allEmps = [];
    employeeMap.forEach(employees => {
      allEmps.push(...employees);
    });
    return allEmps;
  };

  // 获取选中的员工数据
  const getSelectedEmployeesData = () => {
    const selectedEmployees = [];
    
    selectedKeys.forEach(id => {
      // 首先从已展开部门的数据中查找
      let employee = null;
      employeeMap.forEach(employees => {
        if (!employee) {
          employee = employees.find(emp => emp.id === id);
        }
      });
      
      // 如果没找到，从预加载缓存中查找
      if (!employee && preloadedEmployees.has(id)) {
        employee = preloadedEmployees.get(id);
      }
      
      if (employee) {
        selectedEmployees.push(employee);
      }
    });
    
    console.log('获取已选员工数据:', selectedEmployees.length, '个员工');
    return selectedEmployees;
  };

  // 构建数据源（层级）
  const dataSource = buildDataSource(departments);

  // 当存在搜索关键词时，基于全量员工进行全局筛选（姓名 / 账号 / 别名）
  const searchFilteredEmployees = searchKeyword
    ? allEmployees.filter(emp => {
        const kw = searchKeyword.toLowerCase();
        return (
          (emp.name && emp.name.toLowerCase().includes(kw)) ||
          (emp.userid && emp.userid.toLowerCase().includes(kw)) ||
          (emp.alias && emp.alias.toLowerCase().includes(kw))
        );
      })
    : null;

  // 列表数据：搜索时展示拍平结果；无搜索时展示层级数据
  const listData = searchKeyword
    ? (searchFilteredEmployees || []).map(emp => ({
        key: emp.id.toString(),
        title: emp.name,
        description: emp.position || '暂无职位',
        isDepartment: false,
        employee: emp,
        level: 0,
        isInactive: emp.systemStatus !== 1 || emp.status !== 1
      }))
    : dataSource;

  return (
    <Row gutter={16}>
      {/* 左侧：部门和员工选择 */}
      <Col span={12}>
        <Card 
          title={titles[0]} 
          size="small"
          bodyStyle={{ padding: '12px' }}
          style={{ height: '580px' }}
        >
          <div style={{ marginBottom: '12px' }}>
            <Search
              placeholder="搜索员工姓名、账号"
              value={searchKeyword}
              onChange={e => setSearchKeyword(e.target.value)}
              allowClear
              size="small"
            />
          </div>
          
          <Spin spinning={loading}>
            <div style={{ height: '500px', overflowY: 'auto' }}>
              {listData.map(item => {
                const paddingLeft = item.level * 20; // 增加缩进量让层级更明显
                
                if (item.isDepartment) {
                  const isExpanded = expandedDepts.has(item.departmentId);
                  return (
                    <div 
                      key={item.key}
                      style={{ 
                        paddingLeft, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        borderBottom: '1px solid #f0f0f0',
                        backgroundColor: item.level === 0 ? '#fafafa' : (item.level === 1 ? '#f8f8f8' : '#fff'),
                        borderLeft: item.level > 0 ? '2px solid #e6f7ff' : 'none' // 添加左边框表示层级
                      }}
                    >
                      <div 
                        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1 }}
                        onClick={() => toggleDepartment(item.departmentId)}
                      >
                        {isExpanded ? <FolderOpenOutlined style={{ color: '#1890ff' }} /> : <FolderOutlined style={{ color: '#666' }} />}
                        <Text strong style={{ marginLeft: 8, color: item.level === 0 ? '#1890ff' : '#333' }}>
                          {item.title}
                        </Text>
                      </div>
                      <Button 
                        type="link" 
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          addWholeDepartment(item.departmentId);
                        }}
                      >
                        添加部门
                      </Button>
                    </div>
                  );
                } else {
                  const isSelected = selectedKeys.includes(item.employee.id);
                  return (
                    <div 
                      key={item.key}
                      style={{ 
                        paddingLeft,
                        padding: '6px 12px',
                        margin: '1px 0',
                        borderRadius: '4px',
                        backgroundColor: isSelected ? '#e6f7ff' : '#fff',
                        borderLeft: '2px solid #f0f0f0', // 员工项的左边框
                        marginLeft: item.level > 0 ? 10 : 0 // 额外缩进
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleEmployeeSelect(item.employee.id, e.target.checked)}
                      >
                        <Space>
                          <UserOutlined style={{ color: item.isInactive ? '#ccc' : '#52c41a' }} />
                          <Text type={item.isInactive ? 'secondary' : undefined}>
                            {item.title}
                          </Text>
                          {item.employee.alias && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>({item.employee.alias})</Text>
                          )}
                          {item.isInactive && <Tag size="small" color="orange">系统未激活</Tag>}
                        </Space>
                      </Checkbox>
                      {item.description && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', marginLeft: '24px' }}>
                          {item.description}
                        </div>
                      )}
                    </div>
                  );
                }
              })}
            </div>
          </Spin>
        </Card>
      </Col>
      
      {/* 右侧：已选成员 */}
      <Col span={12}>
        <Card 
          title={
            <Space>
              <span>{titles[1]}</span>
              <Text type="secondary">({selectedKeys.length}个)</Text>
            </Space>
          }
          size="small"
          bodyStyle={{ padding: '12px' }}
          style={{ height: '580px' }}
        >
          <div style={{ height: '520px', overflowY: 'auto' }}>
            {getSelectedEmployeesData().length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                暂无选择的成员
              </div>
            ) : (
              <List
                size="small"
                dataSource={getSelectedEmployeesData()}
                renderItem={(employee) => {
                  const isInactive = employee.systemStatus !== 1 || employee.status !== 1;
                  return (
                    <List.Item
                      actions={[
                        <Button 
                          type="link" 
                          size="small" 
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveEmployee(employee.id)}
                          danger
                        >
                          移除
                        </Button>
                      ]}
                      style={{ 
                        padding: '8px 12px',
                        border: '1px solid #f0f0f0',
                        borderRadius: '4px',
                        marginBottom: '8px'
                      }}
                    >
                      <List.Item.Meta
                        avatar={<UserOutlined style={{ color: isInactive ? '#ccc' : '#52c41a' }} />}
                        title={
                          <Space>
                            <Text type={isInactive ? 'secondary' : undefined}>
                              {employee.name}
                            </Text>
                            {employee.alias && (
                              <Text type="secondary" style={{ fontSize: '12px' }}>({employee.alias})</Text>
                            )}
                            {isInactive && <Tag size="small" color="orange">系统未激活</Tag>}
                          </Space>
                        }
                        description={employee.position || '暂无职位'}
                      />
                    </List.Item>
                  );
                }}
              />
            )}
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default DepartmentEmployeeTransfer;